import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Lock, Mail, CalendarClock } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const KEY = "lint_inbox_key";

async function fetchInbox(key) {
  const res = await fetch(`${API}/api/admin/inbox`, { headers: { "X-Admin-Key": key } });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "Could not load inbox.");
  return data;
}

export default function Inbox() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  async function unlock(key) {
    setError("");
    try {
      setData(await fetchInbox(key));
      sessionStorage.setItem(KEY, key);
    } catch (e) {
      sessionStorage.removeItem(KEY);
      setError(e.message);
    }
  }

  useEffect(() => {
    const saved = sessionStorage.getItem(KEY);
    if (saved) unlock(saved);
  }, []);

  return (
    <div className="min-h-screen bg-cream" data-testid="inbox-page">
      <header className="mx-auto flex max-w-5xl items-center justify-between px-5 py-6 sm:px-8">
        <Link to="/" data-testid="inbox-home-link">
          <img src="/bootrise-logo.png" alt="BootRise" className="h-10 w-auto" />
        </Link>
        <span className="text-xs font-semibold uppercase tracking-[0.18em] text-ink-muted">Lint answer inbox</span>
      </header>
      <main className="mx-auto max-w-5xl px-5 pb-20 sm:px-8">
        {data ? <InboxContent data={data} /> : <Unlock onUnlock={unlock} error={error} />}
      </main>
    </div>
  );
}

function Unlock({ onUnlock, error }) {
  const [key, setKey] = useState("");
  return (
    <form
      onSubmit={(e) => { e.preventDefault(); onUnlock(key); }}
      className="mx-auto mt-16 max-w-sm rounded-3xl border border-border bg-surface-elevated p-6 text-center"
      data-testid="inbox-unlock-form"
    >
      <img src="/lint_side_eye.webp" alt="Lint" className="mx-auto h-24" />
      <h1 className="font-display mt-4 text-2xl font-semibold text-navy">Private inbox</h1>
      <p className="mt-1 text-sm text-ink-muted">Enter your passcode to see what founders asked Lint.</p>
      <input
        type="password"
        value={key}
        onChange={(e) => setKey(e.target.value)}
        placeholder="Passcode"
        className="mt-5 w-full rounded-xl border border-border bg-white px-4 py-3 text-sm outline-none ring-sage focus:ring-2"
        data-testid="inbox-passcode-input"
      />
      <button
        type="submit"
        className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-full bg-brand px-5 py-3 text-sm font-semibold text-cream transition-colors hover:bg-navy"
        data-testid="inbox-unlock-btn"
      >
        <Lock size={14} /> Open inbox
      </button>
      {error && <p className="mt-3 text-sm text-critical" data-testid="inbox-error">{error}</p>}
    </form>
  );
}

function InboxContent({ data }) {
  return (
    <div className="grid gap-10 lg:grid-cols-[1.5fr_1fr]">
      <section data-testid="inbox-questions">
        <h2 className="font-display text-2xl font-semibold text-navy">
          Questions asked <span className="text-ink-muted">({data.questions.length})</span>
        </h2>
        <ul className="mt-5 space-y-4">
          {data.questions.map((q, i) => <QuestionCard key={i} q={q} i={i} />)}
          {!data.questions.length && <p className="text-sm text-ink-muted">No questions yet.</p>}
        </ul>
      </section>
      <section data-testid="inbox-bookings">
        <h2 className="font-display text-2xl font-semibold text-navy">Upcoming demos</h2>
        <ul className="mt-5 space-y-3">
          {data.bookings.map((b, i) => (
            <li key={i} className="rounded-2xl border border-border bg-surface-elevated p-4" data-testid={`inbox-booking-${i}`}>
              <p className="flex items-center gap-2 text-sm font-semibold text-navy">
                <CalendarClock size={14} className="text-brand" /> {b.demo_time}
              </p>
              <p className="mt-1 text-sm text-ink-muted">{b.name ? `${b.name} · ` : ""}{b.email}</p>
            </li>
          ))}
          {!data.bookings.length && <p className="text-sm text-ink-muted">No demos booked yet.</p>}
        </ul>
      </section>
    </div>
  );
}

function QuestionCard({ q, i }) {
  return (
    <li className="rounded-3xl border border-border bg-surface-elevated p-5" data-testid={`inbox-question-${i}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-xs text-ink-muted">
          {new Date(q.asked_at).toLocaleString()}{q.turn > 1 ? " · Follow-up" : ""}
        </span>
        {q.email ? (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-sage-soft px-3 py-1 text-xs font-medium text-navy" data-testid={`inbox-question-lead-${i}`}>
            <Mail size={12} /> {q.name ? `${q.name} · ` : ""}{q.email}{q.demo_time ? ` · ${q.demo_time}` : ""}
          </span>
        ) : (
          <span className="rounded-full border border-border px-3 py-1 text-xs text-ink-muted">Anonymous</span>
        )}
      </div>
      <p className="font-display mt-3 text-lg leading-snug text-navy">&ldquo;{q.question}&rdquo;</p>
      <details className="mt-2">
        <summary className="cursor-pointer text-xs font-semibold text-brand">Lint&apos;s answer</summary>
        <p className="mt-2 text-sm leading-relaxed text-ink-muted">{q.answer || "(no answer saved)"}</p>
      </details>
    </li>
  );
}
